import { useAdminPermissions } from "@/hooks/useAdminPermissions";
import { ShieldX } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

interface AdminPermissionGuardProps {
  permission?: string;
  anyPermission?: string[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export default function AdminPermissionGuard({
  permission,
  anyPermission,
  children,
  fallback,
}: AdminPermissionGuardProps) {
  const { hasPermission, hasAnyPermission, loading } = useAdminPermissions();

  if (loading) return <>{children}</>;

  const allowed = permission
    ? hasPermission(permission)
    : anyPermission
    ? hasAnyPermission(anyPermission)
    : true;

  if (!allowed) {
    return fallback ?? (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="max-w-md w-full">
          <CardContent className="flex flex-col items-center gap-4 py-12">
            <div className="p-4 rounded-full bg-destructive/10">
              <ShieldX className="h-10 w-10 text-destructive" />
            </div>
            <h2 className="text-xl font-bold text-foreground">Access Denied</h2>
            <p className="text-muted-foreground text-center text-sm">
              You don't have permission to access this section. Contact your administrator if you believe this is an error.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
